import { loginRedirectUrl } from './client';

// Status reported by the SSE-backed subscriptions (jobs + run streams) and
// rendered by `RefreshPill` via its `kind: 'sse'` prop. There's no fixed
// cadence, so no countdown — just connection liveness. 'ended' is terminal: a
// finite stream (a run that has reached a terminal state) closed cleanly —
// distinct from 'reconnecting', which means an unexpected drop the wrapper is
// retrying.
export type LiveStatus = 'live' | 'paused' | 'reconnecting' | 'error' | 'ended';

const ERROR_THRESHOLD = 3;
const ERROR_WINDOW_MS = 10_000;
// Delay before the wrapper manually reopens a hard-closed EventSource that the
// browser will not auto-reconnect (see `onErr`).
const REOPEN_DELAY_MS = 3_000;
// `EventSource.CLOSED` per the WHATWG spec: the connection failed permanently and
// the browser will NOT auto-reconnect (contrast CONNECTING=0, which it will). Held
// as a literal so it still works under test doubles that omit the static member.
const EVENT_SOURCE_CLOSED = 2;

// Named events the server emits. `subscribeSse` registers a listener per name
// so the consumer's `onEvent(name, data)` callback receives the event type.
// 'job' and 'run' added for A1EE-702: the job-scoped stream
// (GET /jobs/{id}/events) uses these names, and EventSource only delivers
// events we register a listener for.
const NAMED_EVENTS = ['snapshot', 'state', 'log', 'error', 'job', 'run'] as const;

export interface SseHandlers {
  onEvent?: (event: string, data: string) => void;
  onStatus?: (status: LiveStatus) => void;
  /**
   * Called once per failure episode when the connection HARD-closes and a follow-up probe
   * has recovered the real HTTP status the `EventSource` hid (see `probeHardFailure`).
   * `null` means the probe got no HTTP response at all. Never called for a transient drop
   * (the browser is retrying, nothing to report) nor for a clean end-of-stream after
   * `complete()`. Drives the user-visible failure copy — A1EE-364.
   */
  onHardFailure?: (status: number | null) => void;
}

/**
 * Defensive JSON.parse for SSE event payloads. Returns `null` and emits a
 * `console.warn` (with the raw data and event name) on malformed JSON instead
 * of throwing. Defense-in-depth alongside the empty/undefined-data guard in
 * `subscribeSse.handleSuccess` — if a server bug ever sends malformed JSON,
 * the SSE channel doesn't blow up. See A1EE-295.
 */
export function parseSseJson<T>(event: string, data: string): T | null {
  try {
    return JSON.parse(data) as T;
  } catch (err) {
    console.warn(`SSE: failed to parse '${event}' payload`, { data, error: err });
    return null;
  }
}

export interface SseSubscription {
  close: () => void;
  // Mark the stream as terminal (the consumer observed a terminal run state). A
  // finite stream is closed by the *server* once it has flushed the final backfill;
  // EventSource reports that close as an `error`, indistinguishable from a real drop.
  // After `complete()`, the next close is treated as a clean end ('ended', no reconnect)
  // instead of 'reconnecting'. Call it BEFORE the server closes — the terminal signal
  // (snapshot/state `terminal: true`) is delivered ahead of the close, so the consumer
  // can mark completion synchronously from the event handler.
  complete: () => void;
}

/**
 * Open an `EventSource` against [url], auto-pausing on `document.visibilityState`
 * 'hidden' and reopening on 'visible'. Reports lifecycle via `onStatus`. Escalates
 * to the Okta login redirect after 3 errors within 10s without an intervening
 * successful event or connection open (EventSource hides the HTTP status, so a
 * sustained error burst is the only signal we have that auth has expired —
 * mirrors `apiFetch`'s 401 path).
 *
 * The escalation is gated on a prior success: errors are only counted toward the
 * 3-in-10s threshold once this subscription has received at least one real
 * (data-bearing) event. A pure error burst BEFORE any success — a cold pod, a
 * crash-looping deploy, ALB target re-registration — leaves the user in
 * 'reconnecting' indefinitely rather than bouncing them to Okta, because the
 * session is valid and only the backend is (transiently) unreachable. See A1EE-361.
 *
 * The 3-in-10s burst heuristic only fires for TRANSIENT drops, which the browser
 * auto-reconnects (firing an error each attempt). A successful `onopen` (HTTP 200
 * event-stream — auth provably valid) clears the error window, so a flapping but
 * authenticated connection that keeps dropping before its first event can't
 * accrue 3 errors and falsely redirect. A HARD close — an HTTP error status, a
 * non-`text/event-stream` body (e.g. a CloudFront HTML error page), or the
 * browser giving up — sets `readyState` to CLOSED and fires exactly ONE error
 * with no auto-reconnect, so the burst would never trip. We handle it explicitly:
 * after a healthy session, a hard close MAY be the auth-failure signal — but it
 * is equally an ALB 503 during a rolling deploy or a CloudFront error page on a
 * perfectly valid session, so we schedule ONE manual reopen first and escalate
 * to Okta only if that reopen also hard-closes before opening. Before any
 * success, we just keep scheduling our own reopens (the browser won't), so a
 * cold/recovering backend isn't left dead on 'reconnecting' (A1EE-361).
 *
 * The pause trigger is visibility-only, intentionally NOT focus-aware: a tab that is
 * visible-but-not-focused (another window in front) still benefits from a held
 * connection, and churning EventSource open/close on focus blips wastes the
 * snapshot-on-connect round-trip.
 */
export function subscribeSse(url: string, handlers: SseHandlers): SseSubscription {
  let es: EventSource | null = null;
  let recentErrors: number[] = [];
  let stopped = false;
  let terminal = false;
  // Whether this subscription has ever received a real (data-bearing) event.
  // Gates the Okta escalation in `onErr` — see A1EE-361.
  let everSucceeded = false;
  // Pending manual-reopen timer for a hard-closed connection (see `scheduleReopen`).
  let reopenTimer: ReturnType<typeof setTimeout> | null = null;
  // Whether the current connection is the single manual-reopen attempt that
  // follows a post-success hard close. Set when that reopen is scheduled and
  // cleared once a connection opens (auth provably valid); if it hard-closes
  // again while set, we escalate to Okta (see `onErr`).
  let retryingHardClose = false;
  // Whether a hard-failure probe has already run for the CURRENT failure episode. Reset on
  // any successful open, so a later outage probes again. Without it the post-hard-close
  // manual reopen would fire a second redundant request for the same outage.
  let probed = false;

  const setStatus = (status: LiveStatus) => handlers.onStatus?.(status);

  /**
   * Recover the HTTP status `EventSource` refused to give us.
   *
   * `onerror` carries no status and `readyState` only distinguishes CLOSED from CONNECTING,
   * so from inside this wrapper a 404, a 406, a 503 and a DNS failure are indistinguishable
   * — which is why the UI could never explain itself (A1EE-364; same root constraint as the
   * A1EE-323 `forbidden` caveat in CLAUDE.md). On a hard close we just ask the URL directly,
   * once per episode.
   *
   * Deliberately raw `fetch`, NOT `apiFetch`: apiFetch's 401 branch calls
   * `window.location.assign` to begin the Okta redirect, and a diagnostic probe must never
   * navigate the page. The 3-in-10s burst heuristic in `onErr` owns the auth-escalation
   * decision; a probe that pre-empted it would bypass the A1EE-361 gating entirely.
   */
  const probeHardFailure = () => {
    if (probed || stopped || !handlers.onHardFailure) return;
    probed = true;
    const controller = new AbortController();
    void fetch(url, { credentials: 'include', signal: controller.signal }).then(
      (res) => {
        // A 200 here is an INFINITE text/event-stream body. `fetch` resolves on headers, so
        // abort before anything reads it — otherwise probing a healthy endpoint would hold
        // a second connection open for the lifetime of the tab.
        controller.abort();
        // The endpoint answers fine, so the close raced with recovery (or was never really
        // an endpoint problem). There is nothing to tell the user; staying quiet here is
        // what keeps a flapping-but-healthy backend from raising a banner.
        if (res.ok) return;
        if (!stopped) handlers.onHardFailure?.(res.status);
      },
      () => {
        // No HTTP response at all — offline, DNS, connection refused, TLS. A different
        // message from any status the server could have answered with.
        if (!stopped) handlers.onHardFailure?.(null);
      },
    );
  };

  const redirectToOkta = () => {
    // Mirrors apiFetch's 401 path; the page is navigating away, so we don't bother
    // tearing the connection down first.
    window.location.assign(loginRedirectUrl());
  };

  const clearReopen = () => {
    if (reopenTimer != null) {
      clearTimeout(reopenTimer);
      reopenTimer = null;
    }
  };

  // The browser auto-reconnects a transiently-dropped EventSource, but NOT one that
  // hard-closed (readyState CLOSED). Drop the dead handle and reopen ourselves after
  // a short delay so a cold/recovering backend isn't stuck 'reconnecting' forever.
  const scheduleReopen = () => {
    if (stopped || reopenTimer != null) return;
    closeEs();
    reopenTimer = setTimeout(() => {
      reopenTimer = null;
      if (!stopped && document.visibilityState !== 'hidden') open();
    }, REOPEN_DELAY_MS);
  };

  const handleSuccess = (event: string, data: string) => {
    // Browser-internal events on EventSource (e.g. the `error` event fired when
    // the connection drops, or `MessageEvent` constructed without an explicit
    // `data:` line) arrive with `e.data === undefined` or `null`. Drop them
    // before they reach consumers — every downstream listener calls
    // `JSON.parse(data)` and would crash with `SyntaxError: "undefined" is not
    // valid JSON`. Server-emitted named events always include `data: <payload>`.
    // See A1EE-295.
    if (data == null || data === '') return;
    // A real event arrived: the connection is healthy and auth is valid. Mark
    // the session as having succeeded so a later error burst is allowed to
    // escalate (A1EE-361), and reset the error window + hard-close retry state.
    everSucceeded = true;
    recentErrors = [];
    retryingHardClose = false;
    probed = false;
    handlers.onEvent?.(event, data);
  };

  const onErr = () => {
    if (stopped) return;
    if (terminal) {
      // Clean end-of-stream for a finished run, not a dropped connection. Shut
      // down fully (drop the visibility listener too, so a later tab switch can't
      // reopen a finite stream and re-stream the whole log) and report 'ended'.
      stopped = true;
      clearReopen();
      document.removeEventListener('visibilitychange', onVisibility);
      closeEs();
      setStatus('ended');
      return;
    }
    // A hard close (readyState CLOSED) means the connection failed permanently and
    // the browser will NOT auto-reconnect — so it fires exactly one error and the
    // 3-in-10s burst heuristic below would never trip. Handle it explicitly.
    const hardClosed = es?.readyState === EVENT_SOURCE_CLOSED;
    // Probe before the branch split, so BOTH the pre-success path (cold/broken backend) and
    // the post-success path (deploy, expiry, ALB) report a status. A transient drop is
    // skipped on purpose: the browser is already retrying and a message would be noise.
    if (hardClosed) probeHardFailure();
    if (!everSucceeded) {
      // A1EE-361: an error BEFORE any successful event — a cold pod, a
      // crash-looping deploy, or ALB target re-registration — means the backend
      // is unreachable, NOT that auth expired. Bouncing to Okta here is
      // misleading (the session is fine) and fires for every user on a broken
      // deploy. Stay in 'reconnecting' and keep retrying; only escalate once a
      // healthy session has been established (everSucceeded). If the close was
      // hard, the browser won't retry, so schedule our own reopen.
      if (hardClosed) scheduleReopen();
      setStatus('reconnecting');
      return;
    }
    if (hardClosed) {
      if (retryingHardClose) {
        // The single manual reopen ALSO hard-closed before opening. A session
        // that was healthy moments ago now can't even establish a connection
        // twice in a row — that is the auth-failure signal the burst heuristic
        // exists to catch (it fires only one error per close, so the 3-in-10s
        // window would never trip). Escalate to Okta.
        redirectToOkta();
        return;
      }
      // First hard close after a healthy session. This MAY be auth expiry — but
      // it is equally an ALB 503 during a rolling deploy or a CloudFront error
      // page, on a session that is perfectly valid. Don't bounce the user to
      // Okta on one ambiguous signal: schedule ONE manual reopen (the browser
      // won't retry a CLOSED EventSource) and escalate only if it also fails.
      retryingHardClose = true;
      scheduleReopen();
      setStatus('reconnecting');
      return;
    }
    const now = Date.now();
    recentErrors = recentErrors.filter((t) => now - t < ERROR_WINDOW_MS);
    recentErrors.push(now);
    if (recentErrors.length >= ERROR_THRESHOLD) {
      // 3 errors in 10s with no success between them → assume auth failure.
      redirectToOkta();
      return;
    }
    setStatus('reconnecting');
  };

  const open = () => {
    if (stopped || es) return;
    const source = new EventSource(url, { withCredentials: true });
    es = source;
    // Report 'reconnecting' until the connection actually opens — claiming
    // 'live' at construction time made the pill flap live↔reconnecting on every
    // reopen attempt against an unreachable backend (the pre-first-success
    // reopen loop). 'live' is only ever reported from `onopen`.
    setStatus('reconnecting');
    source.onopen = () => {
      // An open connection is an HTTP 200 text/event-stream — auth is provably
      // valid. Clear the error window so a flapping-but-authenticated connection
      // (drops before each first event) can't accrue 3 errors in 10s and falsely
      // redirect, and reset the post-hard-close retry state.
      recentErrors = [];
      retryingHardClose = false;
      // A fresh open ends the failure episode: allow the NEXT outage to probe again.
      probed = false;
      setStatus('live');
    };
    source.onerror = onErr;
    source.onmessage = (e: MessageEvent<string>) => handleSuccess(e.type || 'message', e.data);
    NAMED_EVENTS.forEach((name) => {
      source.addEventListener(name, ((e: MessageEvent<string>) =>
        handleSuccess(name, e.data)) as EventListener);
    });
  };

  const closeEs = () => {
    es?.close();
    es = null;
  };

  const onVisibility = () => {
    if (stopped) return;
    if (document.visibilityState === 'hidden') {
      clearReopen();
      closeEs();
      setStatus('paused');
    } else {
      open();
    }
  };

  document.addEventListener('visibilitychange', onVisibility);

  // Open immediately if visible; if the tab mounts hidden, wait for the
  // first visibilitychange to 'visible'.
  if (document.visibilityState !== 'hidden') {
    open();
  } else {
    setStatus('paused');
  }

  return {
    close: () => {
      stopped = true;
      clearReopen();
      document.removeEventListener('visibilitychange', onVisibility);
      closeEs();
    },
    complete: () => {
      terminal = true;
    },
  };
}
